<template>
    <div class="cost-type-table-wrap">
        <AccountantsCustomerRadioSelector :showModal="showAccountantRadioSelector" @close="showAccountantRadioSelector = false" @accountant-customers-selected="onAccountantCustomersSelected" />

        <b-alert variant="danger" dismissible :show="dismissCountDown" @dismissed="dismissCountDown=0">{{ alertText }}</b-alert>
        <div :class="isAccountantUser ? '' : 'content--inner'">
            <b-table 
                id="settings-cost-type-table"
                :class="'gk-table gk-table--responsive gk-table--striped gk-table--headed light-bg-head gk-table--hover'"
                :items="costTypes"
                :fields="fields"
                :tbody-transition-props="transProps"
                primary-key="name"
                sort-icon-left
                @row-clicked="toggleEdit"
                :tbody-tr-class="rowClass"
            >
                <template v-if="showCreate" slot="top-row">
                    <td><EmojiPicker v-model="newRow.icon"></EmojiPicker></td>
                    <td><b-input type="text" v-model="newRow.name"></b-input></td>
                    <td v-if="isAccountantUser">
                        <LedgerDropdownSearch 
                                :options="costTypeGroupOptions" 
                                @optionSelected="onLedgerOptionSelectedCreate"
                                ref="ledger_dropdown_search_create" />
                    </td>
                    <td v-if="companyCountry=='ES'" style="max-width:150px;">
                        <b-form-select v-if="ledgerOptions.length>0" v-model="newRow.ledger_category">
                            <b-form-select-option v-for="option in ledgerOptions" :value="option.id" :key="option.id" style="display: table-row;">
                                <span style="display: table-cell; min-width:700px;">{{ option.id }}</span> -
                                <span style="display: table-cell;">{{ option.name }}</span>
                            </b-form-select-option>
                        </b-form-select>
                    </td>
                    <td v-if="!isAccountantUser">
                        <b-form-checkbox id="checkbox-0" v-model="newRow.is_balance_sheet" name="checkbox-0" class="checkbox-bg-color" switch />
                    </td>
                    <td><b-form-checkbox id="checkbox-1" v-model="newRow.vat_deductible" name="checkbox-1" class="checkbox-bg-color" switch></b-form-checkbox></td>
                    <td colspan="2" >
                        <GekkoButton 
                            @click="createNew()"
                            :text="$gettext('Create')"
                            icon="fa-solid fa-plus"
                            variant="link" 
                            class="text-primary"
                            size="sm"
                        />
                    </td>
                </template>
                <template #cell(icon)="data">
                    <EmojiPicker v-if="editMode(data.item)" v-model="editingRow.icon"></EmojiPicker>
                    <span v-else class="emoji" v-html="get_icon(data.value)"></span>
                </template>
                <template #cell(name)="data">
                    <b-input v-if="editMode(data.item)" type="text" v-model="editingRow.name"></b-input>
                    <span v-else>{{ data.value }}</span>
                </template>
                <template #cell(vat_deductible)="data">
                    <b-form-checkbox v-if="editMode(data.item)" id="checkbox-2" class="checkbox-bg-color" v-model="editingRow.vat_deductible" name="checkbox-2" switch></b-form-checkbox>
                    <font-awesome-icon v-else :icon="(data.value) ? 'fa-solid fa-check' : 'fa-solid fa-xmark'" />
                </template>
                <template #cell(ledger_category)="data">
                    {{ data.value }}
                </template>
                <template #cell(group)="data">
                    <div>
                        <div v-if="editMode(data.item)">
                            <LedgerDropdownSearch 
                                :options="costTypeGroupOptions" 
                                :defaultOptionText="getCostTypeGroupName(data.value)" 
                                :costTypeId="data.item.id"
                                @optionSelected="onLedgerOptionSelectedEdit" />
                        </div>
                        <span v-if="!editMode(data.item)">
                            <div>{{ getCostTypeGroupName(data.value) }}</div>
                            <div class="group-label-text">{{ getCostTypeGroupBalanceOrVw(data.value) }}</div>
                        </span>
                    </div>
                </template>
                <template #cell(balanceSheet)="data">
                    {{ getCostTypeGroupBalanceOrVw(data.item.group) }}
                </template>
                <template #head(is_balance_sheet)>
                    {{ $gettext('Balance Sheet?') }}
                    <font-awesome-icon :id="'tooltip-cost-type-investment-' + flag" class="text-info tooltip-icon tooltip-icon--question" icon="fa-solid fa-circle-question" />
                    <b-tooltip :custom-class="'tooltip-padded'" :target="'tooltip-cost-type-investment-' + flag" triggers="hover" container="#table-container">
                        <div class="tooltip-title">
                            {{ $gettext('Balance Sheet') }}
                        </div>
                        <div class="tooltip-text">
                            {{ $gettext('When you select this, the cost type will save your costs as an investment on the balance sheet.') }}
                        </div>
                    </b-tooltip>
                </template>
                <template #cell(is_balance_sheet)="data">
                    <div>
                        <b-form-checkbox v-if="editMode(data.item)" id="checkbox-3" class="checkbox-bg-color" v-model="editingRow.is_balance_sheet" name="checkbox-2" switch></b-form-checkbox>
                        <font-awesome-icon v-else :icon="(data.value) ? 'fa-solid fa-check' : 'fa-solid fa-xmark'" />
                        
                        <div v-if="!isAccountantUser && currentCompany.has_accountant" class="group-label-text">{{ getCostTypeGroupName(data.item.group) }}</div>
                    </div>
                </template>
                <template #cell(archived)="data">
                    <CostTypeArchive v-if="!editMode(data.item)" :data_object="data.item" @archive="onArchiveCostType" />
                </template>
                <template #cell(actions)="data">
                    <span v-if="!data.item.is_default">
                        <GekkoButton 
                            v-if="editMode(data.item)" 
                            @click="saveEdit()"
                            :text="$gettext('Save')"
                            icon="fa-solid fa-floppy-disk"
                            variant="link" 
                            class="text-info"
                            size="sm"
                        />
                        <GekkoButton 
                            v-if="editMode(data.item)" 
                            @click="resetEdit(data.item)"
                            :text="$gettext('Cancel')"
                            icon="fa-solid fa-rotate-left"
                            variant="link" 
                            class="text-info"
                            size="sm"
                        />
                    </span>
                </template>
            </b-table>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import UserService from "@/store/services/user.service.js";
import CostTypeService from "@/store/services/costtype.service.js";
import CostTypeGroupService from "@/store/services/costtypegroup.service.js";
import CostTypeArchive from "@/components/cost_types/CostTypeArchive.vue";
import EmojiPicker from "@/components/cost_types/EmojiPicker.vue";
import GekkoButton from "@/components/GekkoButton";
import LedgerDropdownSearch from "@/components/accountants/LedgerDropdownSearch";
import AccountantsCustomerRadioSelector from '@/components/cost_types/AccountantsCustomerRadioSelector.vue';

export default {
    name: "CostTypeTable",
    props: {
        flag: String,
    },
    components: {
        CostTypeArchive,
        EmojiPicker,
        GekkoButton,
        LedgerDropdownSearch,
        AccountantsCustomerRadioSelector
    },
    data() {
        return {
            dismissCountDown: 0,
            showCreate: false,
            editingRow: null,
            archivingRow: null,
            costTypes: [],
            costtypeGroups: [],
            newRow: { company: null, vat_deductible:false, icon: '1f604' },
            transProps: {
                name: 'flip-list'
            },
            ledgerOptions:[],
            costTypeGroupOptions: [],
            fields: [
                { key: 'icon',label: this.$gettext('Icon'), sortable: false },
                { key: 'name',sortable: true,label: this.$gettext('Name'), tdClass: 'col-name' },
                { key: 'ledger_category',label: this.$gettext('Ledger code') },
                { key: 'group',label: this.$gettext('Ledger')},
                { key: 'balanceSheet',label: this.$gettext('Balance Sheet'),tdClass: 'd-none',thClass: 'd-none' },
                { key: 'is_balance_sheet', label: '', sortable: true },
                { key: 'vat_deductible',label: this.$gettext('Vat deductible?'),sortable: true },
                { key: 'archived',label: this.$gettext('Archived'),sortable: true },
                { key: 'actions',label: '',sortable: false }
            ],
            BALANCESHEET_OR_VW: {
                '': 'Empty',
                'balance': this.$gettext('Balance Sheet'),
                'profit': this.$gettext('P&L')
            },
            isAccountantUser: false,
            companyCountry: '',
            is_premium: false,
            alertText: null,
            showAccountantRadioSelector: false,
            accountant_ledger_action: null // 'update', 'archive' or 'create'
        }
    },
    computed: {
        ...mapGetters({ 
            currentCompany: "main/getCurrentCompany",
            getCompanyId: 'main/getCompanyId'
        }),
    },
    created(){
        this.setupData();
    },
    methods: {
        setupData() {
            this.newRow.company = this.getCompanyId;
            this.companyCountry = this.currentCompany.country;
            this.showCreate = !this.handleFlag();
            CostTypeService.list({ is_default: this.handleFlag(), company: this.getCompanyId }).then((response) => {
                this.costTypes = response.data;
            });
            this.handleColumns();
        },
        handleColumns(){
            var vm = this;
            UserService.retrieveCurrent({extended: 'True'}).then(function(response){
                vm.is_premium = response.data.is_premium;
                vm.isAccountantUser = response.data.is_accountant;

                var excluded_columns = [];
                
                if(vm.companyCountry != 'ES'){
                    excluded_columns.push('ledger_category');
                }

                if (vm.isAccountantUser) {
                    excluded_columns.push('is_balance_sheet');
                    vm.handleAccountant();
                }else{
                    excluded_columns.push('group', 'balanceSheet');
                    if(vm.companyCountry === 'ES'){
                        if(vm.showCreate){
                            CostTypeService.spanishLedgerOptions().then(function(response){
                                vm.ledgerOptions = response.data;
                            })
                        }
                    }

                    if (vm.currentCompany.has_accountant) {
                        CostTypeGroupService.getConnectedAccountantGroups({'company_profile': vm.getCompanyId}).then(function(response) {
                            vm.costtypeGroups = response.data;
                        });
                    }
                }

                var i = 0;
                while(i<vm.fields.length){
                    if(excluded_columns.includes(vm.fields[i].key)){
                        vm.fields.splice(i, 1);
                    }else{
                        i++
                    }
                }
            });
        },
        handleFlag(){
            if(this.$props.flag == "default") {
                return true;
            }else if(this.$props.flag == "custom") {
                return false;
            }else{
                return null;
            }
        },
        handleAccountant(){
            var vm = this;
            
            CostTypeGroupService.list().then(function (response) {
                vm.costtypeGroups = response.data;
                
                vm.costTypeGroupOptions = vm.costtypeGroups;
                
                for (var i = 0; i < vm.costtypeGroups.length; i++) {
                    vm.costtypeGroups[i].balance_vw = vm.BALANCESHEET_OR_VW[vm.costtypeGroups[i].balance_or_vw];
                }
                
                // Add generic cost type group for balance sheet items marked by users
                CostTypeGroupService.getBalanceSheetGroup().then(function(response) {
                    vm.costtypeGroups.push(response.data);
                });
            });
        },
        onLedgerOptionSelectedCreate(option) {
            this.newRow.group = option.id;
        },
        onLedgerOptionSelectedEdit(option) {
            this.editingRow.group = option.id;
        },
        getCostTypeGroupName(group_id) {
            var group = this.costtypeGroups.filter(d => d.id === group_id);
            if (group && group.length) {
                return group[0].name;
            }
        },
        getCostTypeGroupBalanceOrVw(group_id) {
            var group = this.costtypeGroups.filter(d => d.id === group_id);
            if (group && group.length) {
                return group[0].balance_vw;
            }
        },
        get_icon(icon) {
            return "&#x" + icon + ";";
        },
        rowClass(item){
            if(item && item.archived){
                return "row-archived";
            }
            return "row-unarchived";
        },
        toggleEdit(item){
            if(!this.showCreate || item.archived){
                return;
            }
            if(this.editingRow && this.editingRow.id == { ...item }.id){
                return;
            }
            let editing = true

            if (this.editingRow) {
                editing = false
                const ConfirmModalConstructor = this.$options.components['confirm-modal']
                const confirmModalInstance = new ConfirmModalConstructor({ propsData: {
                    title: this.$gettext('Unsaved changes'),
                    message: this.$gettext('You have unsaved changes, are you sure you want to continue?'),
                    options: {
                        variant: 'primary',
                        confirmText: this.$gettext('Ok')
                    }
                }})
                confirmModalInstance.$mount()
                document.body.appendChild(confirmModalInstance.$el)
                confirmModalInstance.$on('confirmed', () => {
                    if (confirmModalInstance && confirmModalInstance.$el.parentNode) {
                        confirmModalInstance.$el.parentNode.removeChild(confirmModalInstance.$el)
                    }
                    this.editingRow = { ...item }
                })
                confirmModalInstance.$on('cancel', () => {
                    if (confirmModalInstance && confirmModalInstance.$el.parentNode) {
                        confirmModalInstance.$el.parentNode.removeChild(confirmModalInstance.$el)
                    }
                })
            }
            if (editing) {
                this.editingRow = { ...item };
            }
        },
        resetEdit() {
            this.editingRow = null;
            this.archivingRow = null;
        },
        editMode(item){
            return this.editingRow && this.editingRow.id === item.id;
        },
        saveEdit() {
            if (this.isAccountantUser && this.$route.path.includes('ledger')) {
                this.accountant_ledger_action = 'update';
                this.showAccountantRadioSelector = true;
                return;
            }
            
            let data = CostTypeService.prepareDataForUpdate(this.editingRow);
            this.saveCostType(data);
        },
        onArchiveCostType(costType) {
            this.archivingRow = costType;
            if (this.isAccountantUser && this.$route.path.includes('ledger')) {
                this.accountant_ledger_action = 'archive';
                this.showAccountantRadioSelector = true;
                return;
            }
            
            let data = CostTypeService.prepareDataForUpdate(costType);
            data.is_archived = !data.is_archived;
            this.saveCostType(data);
        },
        onAccountantCustomersSelected(selection) {
            let data = {}
            data.accountant_customers_mode = selection.mode;

            if (selection.mode === 'all') {
                data.accountant_customers = 'all';
            } else {
                data.accountant_customers = selection.customerIds;
            }

            if (this.accountant_ledger_action === 'update') {
                data = {
                    ...data,
                    ...CostTypeService.prepareDataForUpdate(this.editingRow)
                }
                this.saveCostType(data);
            } else if (this.accountant_ledger_action === 'archive') {
                data = {
                    ...data,
                    ...CostTypeService.prepareDataForUpdate(this.archivingRow)
                }
                data.is_archived = !data.is_archived;
                this.saveCostType(data);
            } else {
                data = {
                    ...data,
                    ...CostTypeService.prepareDataForUpdate(this.newRow)
                }
                this.createCostType(data);
            }
        },
        saveCostType(data) {
            let item = this.costTypes.find((u) => u.id === (this.editingRow ? this.editingRow.id : this.archivingRow.id));
            
            var isPremium = this.$store.getters['getIsPremium'];
            if (isPremium || this.isAccountantUser) {
                CostTypeService.update(data).then((response) => {
                    Object.assign(item, this.editingRow);
                    this.$emit("costTypeUpdated", response.data);
                    this.resetEdit();
                    this.$store.commit('setAlert', {
                        title: this.$gettext("Success"),
                        text: this.$gettext("The cost type was successfully updated."),
                        variant: "success",
                        duration: 4
                    });
                }).catch((error) => {
                    if(error.status == 400) {
                        this.alertText = 'non_field_errors' in error.data && error.data['non_field_errors'].length ? 
                            error.data['non_field_errors'][0] : this.$gettext('Something went wrong')
                        
                        this.dismissCountDown = 3;
                    } else {
                        this.$store.commit('setAlert', {
                            title: this.$gettext("Error"),
                            text: this.$gettext("The cost type could not be updated."),
                            variant: "danger",
                            duration: 4
                        });
                    }
                });
            } else {
                this.$store.commit('togglePremiumModal', 'web_costtype_create');
            }
        },
        createNew() {
            var isPremium = this.$store.getters['getIsPremium'];
            if (isPremium || (this.isAccountantUser && !this.$route.path.includes('ledger'))) {
                this.createCostType(CostTypeService.prepareDataForUpdate(this.newRow));
            } else if (this.isAccountantUser && this.$route.path.includes('ledger')) {
                this.showAccountantRadioSelector = true;
                return;
            } else {
                this.$store.commit('togglePremiumModal', 'web_costtype_create');
            }
        },
        createCostType(data) {
            CostTypeService.create(data).then((response) => {
                this.costTypes.push(response.data);
                this.$emit("costTypeCreated", response.data);
                this.$refs.ledger_dropdown_search_create.clearData();
                this.newRow = { company: this.getCompanyId, vat_deductible: false, icon: '1f604' };
            }).catch((response) => {
                switch (response.status) {
                    case 500:
                        this.$store.commit('setAlert', {
                            title: this.$gettext("Something went wrong"),
                            text: this.$gettext("Please contact us regarding this error, and try again later."),
                            variant: "error",
                            duration: 4
                        });
                        break;
                    case 400:
                        var message;
                        if (this.newRow.name == null) {
                            message = "The name field cannot be empty!";
                        }
                        else {
                            message = "There already exists a cost with that name!";
                        }
                        this.$store.commit('setAlert', {
                            title: this.$gettext("Wrong data"),
                            text: this.$gettext(message),
                            variant: "error",
                            duration: 4
                        });
                        break;
                }
            })
        }
    }
};
</script>
<style lang="scss">
table.table-transition .flip-list-move {
  transition: transform 0.3s;
}
.checkbox-bg-color .custom-control-input:checked ~ .custom-control-label::before{
    border-color: #0575ff;
    background-color: #0575ff;
}
.row-archived{
    color: gray;
    filter: grayscale(100%);
}
.row-unarchived:hover{
    cursor: pointer;
}
.row-archived:hover{
    color: gray !important;
    filter: grayscale(100%);
}
</style>

<style scoped>
.group-label-text {
    font-size: 0.85em;
    color: #b9b4b4;
}
</style>
